import {
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { createDocument, isDocIdTaken } from '@/data/util_documentIds';
import { db } from '@/firebaseInit';
import { normaliseShortCode } from '@/types/types_code';
import { coll_experiences, normaliseExperience, type ExperienceType } from '@/types/types_experience';

// Data-access layer for experiences, backed by Firestore.
//
// Each document lives in the `experiences` collection with its document id equal
// to the experience `id`. This module is the single place that talks to Firestore
// for experiences — pages and hooks depend only on the async functions below, so
// the storage backend can change here without touching the UI.
//
// Seeding is done separately by `scripts/importExperiences.ts`.

const experiencesCollection = collection(db, coll_experiences);

function toExperience(id: string, data: Record<string, unknown>): ExperienceType {
  return normaliseExperience(data as Partial<ExperienceType>, id);
}

export async function listExperiences(): Promise<ExperienceType[]> {
  const snap = await getDocs(experiencesCollection);
  return snap.docs.map((d) => toExperience(d.id, d.data()));
}

/**
 * Subscribes to all experiences, invoking `onChange` with the current list on
 * the initial snapshot and again on every subsequent change. Returns an
 * unsubscribe function to detach the listener.
 */
export function subscribeExperiences(
  onChange: (experiences: ExperienceType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    experiencesCollection,
    (snap) => onChange(snap.docs.map((d) => toExperience(d.id, d.data()))),
    onError,
  );
}

/**
 * Just the names of the experiences named, keyed by id.
 *
 * For putting a title on a handful of ids — a chart's top five, a list of
 * references — without subscribing to the whole catalogue. An experience
 * document carries its full copy and its edit history, so fetching every one of
 * them to label five rows is far and away the most expensive thing such a page
 * would do.
 *
 * Firestore caps an `in` filter at 30 values. Well above what the callers ask
 * for, so ids past that are dropped rather than paged: a caller wanting more
 * than thirty names wants the subscription instead.
 */
export async function getExperienceNames(ids: string[]): Promise<Map<string, string>> {
  if (ids.length === 0) return new Map();
  const snap = await getDocs(query(experiencesCollection, where(documentId(), 'in', ids.slice(0, 30))));
  return new Map(snap.docs.map((d) => [d.id, (d.data() as ExperienceType).name]));
}

/**
 * Whether some *other* experience already holds this code.
 *
 * A one-shot query rather than a rule, because Firestore cannot express
 * uniqueness across documents: nothing here stops two admins claiming the same
 * code in the same second. It is the check that matters in practice — the desk
 * mistypes a code far more often than two people mint one at once — and the
 * cost of losing that race is a duplicate label, not a lost booking.
 *
 * `excludeId` is the experience being edited, so re-saving a record does not
 * report it as a clash with itself. A blank code is never taken: most of the
 * catalogue has none.
 */
export async function isExperienceCodeTaken(code: string, excludeId?: string): Promise<boolean> {
  const normalised = normaliseShortCode(code);
  if (!normalised) return false;
  const snap = await getDocs(query(experiencesCollection, where('code', '==', normalised)));
  return snap.docs.some((d) => d.id !== excludeId);
}

export async function getExperience(id: string): Promise<ExperienceType | undefined> {
  const snap = await getDoc(doc(experiencesCollection, id));
  return snap.exists() ? toExperience(snap.id, snap.data()) : undefined;
}

/**
 * Subscribes to a single experience by id, invoking `onChange` with the current
 * value (or undefined if it doesn't exist) on every change. Returns an
 * unsubscribe function.
 */
export function subscribeExperience(
  id: string,
  onChange: (experience: ExperienceType | undefined) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(experiencesCollection, id),
    (snap) => onChange(snap.exists() ? toExperience(snap.id, snap.data()) : undefined),
    onError,
  );
}

/** Experiences belonging to a collection. */
export async function getExperiencesByCollection(collectionId: string): Promise<ExperienceType[]> {
  const snap = await getDocs(query(experiencesCollection, where('collectionId', '==', collectionId)));
  return snap.docs.map((d) => toExperience(d.id, d.data()));
}

/**
 * Whether an experience already exists under this id. See `isDocIdTaken` — this
 * is the answer for a form to show while somebody types, not the thing that
 * keeps the id unique.
 */
export async function isExperienceIdTaken(id: string): Promise<boolean> {
  return isDocIdTaken(experiencesCollection, id);
}

/**
 * Creates an experience, refusing an id another experience already holds.
 *
 * Editing goes through `updateExperienceFields` (the form autosaves per field),
 * so nothing here needs to double as an update — which is what lets it fail on
 * an id in use rather than write over what is there.
 */
export async function createExperience(experience: ExperienceType): Promise<ExperienceType> {
  return createDocument(experiencesCollection, experience);
}

/**
 * Field-level update of a single experience, used by autosave. `updateObj` may
 * contain dotted field paths for nested updates. `updatedAt` is stamped here so
 * callers only need to pass the changed fields.
 */
export async function updateExperienceFields(
  id: string,
  updateObj: Record<string, unknown>,
): Promise<void> {
  await updateDoc(doc(experiencesCollection, id), {
    ...updateObj,
    updatedAt: new Date().toISOString(),
  });
}

/** Deletes an experience. */
export async function deleteExperience(id: string): Promise<void> {
  await deleteDoc(doc(experiencesCollection, id));
}

/**
 * Field-level update of several experiences at once, used by the featured-order
 * autosave. Each `updateObj` is a complete Firestore update — dotted field paths
 * and `deleteField()` sentinels included — so history steps ride along with the
 * fields they describe.
 *
 * One batch, so the ordering never ends up half-written: a renumber that applied
 * to three of five experiences would leave two positions claimed twice.
 *
 * Firestore takes at most 500 writes in a batch, which the home page's featured
 * section is in no danger of reaching.
 */
export async function updateExperienceFieldsBatch(
  updates: { id: string; updateObj: Record<string, unknown> }[],
): Promise<void> {
  if (updates.length === 0) return;
  const now = new Date().toISOString();
  const batch = writeBatch(db);
  for (const { id, updateObj } of updates) {
    batch.update(doc(experiencesCollection, id), { ...updateObj, updatedAt: now });
  }
  await batch.commit();
}
